import { useState } from 'react'
import { usePetState, type Species, type Stage, type Emotion } from '@/hooks/usePetState'
import { getSpeciesName } from '@/lib/speciesNames'
import { getPetImageUrlForRuntime, getPetVariantUrl } from '@/lib/petImages'

/**
 * PetAvatar — focus-you Memory v6 Pet Companion
 *
 * 関連 spec: .company/departments/ux-design/2026-05-04-pet-companion-design-spec.md (v0.3)
 * 関連 decision: growth_events 64d50a56 (v4 関係性), 5d12c731 (v6 変態), 74c8de2a (v7 L0更新)
 * 関連 task   : 2026-05-05 designer → sys-dev (PNG 化 + Storage 統合)
 *
 * 設計原則 (spec §0):
 *  - 形ではなく "気配" を設計する (susuwatari/kodama)
 *  - 状態と理解度は独立変数: 表情 = 短期 emotion, 姿 = 長期 coverage
 *  - 「会わない日があっても拗ねない」: 痩せない・怒らない・消えない
 *  - silence-first: 進化通知ゼロ
 *
 * 描画レイヤー (2026-05-05 PNG 統合):
 *  - egg / hatching (cocoon 含む) : 既存 SVG (CocoonShape, EggShape) を維持
 *  - hatch / juvenile / mature / sage : Supabase Storage `pet-companions` の PNG
 *  - PNG ロード失敗時 (404 / network) : SVG silhouette にフォールバック
 *  - 息遣い・瞬きは <img> でも CSS transform で同様に表現
 */
export function PetAvatar({ size = 96, showCoverageHint = false }: { size?: number; showCoverageHint?: boolean }) {
  const { state, loading } = usePetState()

  if (loading) {
    return <div style={{ width: size, height: size }} aria-hidden="true" />
  }

  // L0 が空の場合 = まだ何も知らない = 卵
  // 変態中 (cocoon) は専用形態。cocoon 後半 (6日以上経過) は脱皮アニメ
  const inCocoon = state?.inCocoon ?? false
  const hatching = state?.hatching ?? false
  const stage: Stage = inCocoon ? 'egg' : (state?.stage ?? 'egg')
  const species: Species = state?.species ?? 'kitsune'
  const emotion: Emotion = state?.emotion ?? 'calm'
  const coverage = state?.coverage ?? 0
  const derivedCA = state?.derivedCA ?? null

  return (
    <div
      role="img"
      aria-label={`Pet companion: ${getSpeciesName(species)} (${stage}, ${emotion})`}
      style={{
        width: size,
        height: size,
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <PetVisual species={species} stage={stage} emotion={emotion} size={size} coverage={coverage} inCocoon={inCocoon} hatching={hatching} derivedCA={derivedCA} />
      {showCoverageHint && coverage > 0 && (
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            bottom: -16,
            left: 0,
            right: 0,
            textAlign: 'center',
            fontSize: 10,
            color: 'var(--text2)',
            opacity: 0.6,
          }}
          title={`理解度: ${coverage}%`}
        >
          ・{coverage}%
        </div>
      )}
    </div>
  )
}

interface VisualProps {
  species: Species
  stage: Stage
  emotion: Emotion
  size: number
  coverage: number
  inCocoon: boolean
  hatching: boolean
  derivedCA: string | null
}

/**
 * 描画ディスパッチャ: stage に応じて PNG (img) か SVG を出し分ける。
 * 息遣い・オーラのアニメーションはここで一元管理。
 */
function PetVisual({ species, stage, emotion, size, coverage, inCocoon, hatching, derivedCA }: VisualProps) {
  // ステージごとのサイズ感 (spec §6 のニュアンス: 卵 → 老成で輪郭が固まる)
  const scale = stage === 'egg' ? 0.55 : stage === 'hatch' ? 0.7 : stage === 'juvenile' ? 0.85 : stage === 'mature' ? 1.0 : 1.05
  // emotion ごとの息遣い周期 (spec §7.1)
  const breathSec =
    emotion === 'joy' || emotion === 'curious' ? 2 :
    emotion === 'sad' || emotion === 'tired' ? 6 :
    emotion === 'tense' ? 1.5 :
    4

  // 卵 / 繭 / 脱皮中は SVG を維持 (PNG は通常状態のみ)
  const useSvg = inCocoon || stage === 'egg'

  // 共通の scale/breath アニメ用 inline style
  const breatheStyle: React.CSSProperties = {
    width: size,
    height: size,
    animation: `pet-breathe ${breathSec}s ease-in-out infinite`,
    transformOrigin: 'center 80%',
  }

  return (
    <>
      {/* グローバル keyframes (各インスタンスで重複定義になるが影響は無視できる) */}
      <style>{`
        @keyframes pet-breathe {
          0%, 100% { transform: scale(${scale}); }
          50% { transform: scale(${scale * 1.03}); }
        }
      `}</style>

      {useSvg ? (
        <PetSvg stage={stage} emotion={emotion} size={size} inCocoon={inCocoon} hatching={hatching} breatheStyle={breatheStyle} />
      ) : (
        <PetImage species={species} stage={stage} emotion={emotion} size={size} breatheStyle={breatheStyle} derivedCA={derivedCA} />
      )}

      {/* coverage が dense (>=66%) なら気配オーラ (spec §15.7).
          PNG 描画時もこのオーラは SVG レイヤーで重ねる。 */}
      {stage !== 'egg' && coverage >= 66 && (
        <svg
          width={size}
          height={size}
          viewBox="0 0 100 100"
          xmlns="http://www.w3.org/2000/svg"
          style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}
          aria-hidden="true"
        >
          <circle cx="50" cy="55" r="40" fill="none" stroke="var(--accent, #5046E5)" strokeWidth="0.3" opacity="0.3">
            <animate attributeName="r" values="40;42;40" dur="6s" repeatCount="indefinite" />
            <animate attributeName="opacity" values="0.3;0.15;0.3" dur="6s" repeatCount="indefinite" />
          </circle>
        </svg>
      )}
    </>
  )
}

/**
 * PNG 画像レイヤー。Storage から読み出し、失敗時は SVG silhouette にフォールバック。
 */
function PetImage({
  species,
  stage,
  emotion,
  size,
  breatheStyle,
  derivedCA,
}: {
  species: Species
  stage: Stage
  emotion: Emotion
  size: number
  breatheStyle: React.CSSProperties
  derivedCA: string | null
}) {
  const [imgFailed, setImgFailed] = useState(false)
  // mature stage で derived_ca が確定済み (LH/HH/HL/LL のいずれか) なら variant URL を使う
  // (null = CA 未推定 → default mature)
  const useVariant = stage === 'mature' && (derivedCA === 'LH' || derivedCA === 'HH' || derivedCA === 'HL' || derivedCA === 'LL')
  const url = useVariant
    ? getPetVariantUrl(species, derivedCA)
    : getPetImageUrlForRuntime(species, stage, emotion)

  // URL 未生成 (env 未設定) または読み込み失敗 → SVG fallback
  if (!url || imgFailed) {
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 100 100"
        xmlns="http://www.w3.org/2000/svg"
        style={breatheStyle}
        aria-hidden="true"
      >
        <SpeciesSilhouette species={species} stage={stage} />
      </svg>
    )
  }

  return (
    <img
      src={url}
      alt=""
      width={size}
      height={size}
      loading="lazy"
      decoding="async"
      onError={() => setImgFailed(true)}
      style={{
        ...breatheStyle,
        objectFit: 'cover',
        objectPosition: 'center',
        // PNG は背景透過の前提。背景が必要ならコンテナ側で指定。
        userSelect: 'none',
        pointerEvents: 'none',
      }}
      draggable={false}
    />
  )
}

/**
 * SVG レイヤー (egg / cocoon / hatching 専用).
 */
function PetSvg({
  stage,
  emotion,
  size,
  inCocoon,
  hatching,
  breatheStyle,
}: {
  stage: Stage
  emotion: Emotion
  size: number
  inCocoon: boolean
  hatching: boolean
  breatheStyle: React.CSSProperties
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      xmlns="http://www.w3.org/2000/svg"
      style={breatheStyle}
    >
      <style>{`
        @keyframes pet-blink {
          0%, 92%, 100% { transform: scaleY(1); }
          95% { transform: scaleY(0.1); }
        }
        .pet-eye { animation: pet-blink ${5 + Math.random() * 7}s ease-in-out infinite; transform-origin: center; }
      `}</style>

      {inCocoon ? (
        <CocoonShape hatching={hatching} />
      ) : stage === 'egg' ? (
        <EggShape emotion={emotion} />
      ) : null}
    </svg>
  )
}

function CocoonShape({ hatching = false }: { hatching?: boolean }) {
  // 繭 (変態中): 半透明の繭の中で影が静かに揺らぐ。柔らかい光が滲む。
  // spec §15.3: 蛹期 (cocoon) 1-7日、息遣いは継続、日常に支障なし
  // hatching=true: 殻が割れ、光が漏れ、新しい姿が顔を出す 24h transition (spec §15.3 脱皮アニメーション)
  if (hatching) {
    return (
      <>
        {/* 強い光のオーラ (脱皮時) */}
        <ellipse cx="50" cy="55" rx="40" ry="46" fill="#FFF7E0" opacity="0.5">
          <animate attributeName="opacity" values="0.5;0.8;0.5" dur="2s" repeatCount="indefinite" />
          <animate attributeName="rx" values="40;44;40" dur="2s" repeatCount="indefinite" />
        </ellipse>
        {/* 殻の片側 (上方向にずれる) */}
        <path d="M 30 35 Q 50 25 70 35 L 70 50 Q 50 45 30 50 Z" fill="#E8DCC0" stroke="#A89878" strokeWidth="0.5" opacity="0.75">
          <animateTransform attributeName="transform" type="translate" values="0 0; 0 -10; 0 -10" dur="3s" repeatCount="indefinite" />
        </path>
        {/* 殻の片側 (下方向にずれる) */}
        <path d="M 30 75 Q 50 85 70 75 L 70 60 Q 50 65 30 60 Z" fill="#E8DCC0" stroke="#A89878" strokeWidth="0.5" opacity="0.75">
          <animateTransform attributeName="transform" type="translate" values="0 0; 0 10; 0 10" dur="3s" repeatCount="indefinite" />
        </path>
        {/* 中から覗く光球 (新しい姿の前兆) */}
        <circle cx="50" cy="55" r="10" fill="#FFF7E0" opacity="0.9">
          <animate attributeName="r" values="8;12;10" dur="2s" repeatCount="indefinite" />
        </circle>
        <circle cx="50" cy="55" r="4" fill="#FFD980" opacity="1.0">
          <animate attributeName="r" values="3;5;4" dur="2s" repeatCount="indefinite" />
        </circle>
        {/* 飛散する粒子 */}
        <circle cx="35" cy="42" r="1" fill="#D4C8A8" opacity="0.8">
          <animate attributeName="cx" values="35;28;35" dur="3s" repeatCount="indefinite" />
          <animate attributeName="cy" values="42;38;42" dur="3s" repeatCount="indefinite" />
        </circle>
        <circle cx="65" cy="48" r="0.8" fill="#D4C8A8" opacity="0.7">
          <animate attributeName="cx" values="65;72;65" dur="4s" repeatCount="indefinite" />
          <animate attributeName="cy" values="48;42;48" dur="4s" repeatCount="indefinite" />
        </circle>
        <circle cx="42" cy="68" r="0.7" fill="#D4C8A8" opacity="0.6">
          <animate attributeName="cy" values="68;75;68" dur="3.5s" repeatCount="indefinite" />
        </circle>
      </>
    )
  }
  // 通常の繭期 (変態前半)
  return (
    <>
      {/* 外側のオーラ (柔らかい光) */}
      <ellipse cx="50" cy="55" rx="30" ry="36" fill="#F5EFE0" opacity="0.4">
        <animate attributeName="rx" values="30;32;30" dur="5s" repeatCount="indefinite" />
        <animate attributeName="ry" values="36;38;36" dur="5s" repeatCount="indefinite" />
      </ellipse>
      {/* 繭本体 */}
      <ellipse cx="50" cy="55" rx="20" ry="28" fill="#E8DCC0" stroke="#A89878" strokeWidth="0.5" opacity="0.85" />
      {/* 繭の縦縞 (シルクの繊維) */}
      <path d="M 38 35 Q 42 55 38 78" stroke="#B8A888" strokeWidth="0.4" fill="none" opacity="0.5" />
      <path d="M 50 32 Q 50 55 50 80" stroke="#B8A888" strokeWidth="0.4" fill="none" opacity="0.5" />
      <path d="M 62 35 Q 58 55 62 78" stroke="#B8A888" strokeWidth="0.4" fill="none" opacity="0.5" />
      {/* 中の影 (静かに動く新しい姿) */}
      <ellipse cx="50" cy="58" rx="6" ry="8" fill="#7A6A5A" opacity="0.3">
        <animate attributeName="cy" values="56;60;56" dur="6s" repeatCount="indefinite" />
        <animate attributeName="opacity" values="0.3;0.45;0.3" dur="6s" repeatCount="indefinite" />
      </ellipse>
      {/* 微かな粒子 (2-3個浮遊) */}
      <circle cx="40" cy="40" r="0.8" fill="#D4C8A8" opacity="0.7">
        <animate attributeName="cy" values="40;38;40" dur="4s" repeatCount="indefinite" />
      </circle>
      <circle cx="62" cy="48" r="0.6" fill="#D4C8A8" opacity="0.6">
        <animate attributeName="cy" values="48;46;48" dur="5s" repeatCount="indefinite" />
      </circle>
    </>
  )
}

function EggShape({ emotion }: { emotion: Emotion }) {
  const fill = emotion === 'sad' || emotion === 'tired' ? '#D4D4DA' : '#E8E4F0'
  return (
    <>
      <ellipse cx="50" cy="55" rx="22" ry="28" fill={fill} stroke="#B8B0C8" strokeWidth="0.4" />
      {/* 中の影だけが少し動く (spec §6 egg) */}
      <circle cx="50" cy="58" r="3" fill="#9A8FB8" opacity="0.25">
        <animate attributeName="cy" values="56;60;56" dur="5s" repeatCount="indefinite" />
      </circle>
    </>
  )
}

/**
 * PNG ロード失敗時の SVG silhouette fallback (8 種共通).
 * 既存の SpeciesPlaceholder を簡略化したもの (種別ラベルだけ表示).
 */
function SpeciesSilhouette({ species, stage }: { species: Species; stage: Stage }) {
  const palette: Partial<Record<Species, string>> = {
    kitsune: '#D9C4A3',
    lupus: '#A0A8B0',
    avian: '#B0C4DE',
    lutra: '#8FA890',
    moss: '#7A9070',
    lumen: '#E8D88A',
    cervus: '#B89878',
    baku: '#9890A8',
  }
  const bodyColor = palette[species] ?? '#B89898'
  const bodyRy = stage === 'hatch' ? 18 : stage === 'juvenile' ? 22 : stage === 'mature' ? 24 : 26

  return (
    <>
      <ellipse cx="50" cy="60" rx="20" ry={bodyRy} fill={bodyColor} opacity="0.85" />
      <ellipse cx="50" cy="48" rx="13" ry="12" fill={bodyColor} />
      <circle cx="45" cy="49" r="1.4" fill="#3A2818" />
      <circle cx="55" cy="49" r="1.4" fill="#3A2818" />
      <text
        x="50"
        y="80"
        textAnchor="middle"
        fontSize="6"
        fill="var(--text2)"
        fontFamily="serif"
        opacity="0.6"
      >
        {getSpeciesName(species)}
      </text>
    </>
  )
}
